import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { AdminAuctionSale } from '@nucleus-commerce/api-client/admin';
import { fromZonedInput } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { MediaIdField, MediaRichTextEditor } from '@/features/media';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { mapSaleField, saleToForm, toUpdateSale } from '../api/sale-mappers';
import { useUpdateSale } from '../hooks/use-sale-mutations';
import {
  saleFormSchema,
  type SaleFormInput,
  type SaleFormOutput,
} from '../schemas/sale-form-schema';
import { isScheduleEditable } from '../schemas/sale-rules';

export const SALE_FORM_ID = 'sale-form';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'title',
  'handle',
  'subtitle',
  'coverMediaId',
  'essay',
  'viewing',
  'startsAt',
  'firstCloseAt',
  'lotIntervalMinutes',
  'buyerPremiumPercent',
]);

export interface SaleFormProps {
  storeId: string;
  sale: AdminAuctionSale;
  timeZone: string;
  canWrite: boolean;
}

/**
 * The sale's words and pictures in the main column, its schedule in the rail. The schedule
 * locks once the sale is scheduled; the editorial fields stay open in every status. Registers
 * with the save bar, which owns Save, Discard and the leave-page guard.
 */
export function SaleForm({ storeId, sale, timeZone, canWrite }: SaleFormProps) {
  const { t } = useTranslation(['auctions', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const update = useUpdateSale(storeId);
  const scheduleEditable = isScheduleEditable(sale);
  const form = useForm<SaleFormInput, unknown, SaleFormOutput>({
    resolver: useI18nResolver(
      saleFormSchema((value) => {
        const iso = fromZonedInput(value, timeZone);
        return iso ? Date.parse(iso) : null;
      }, scheduleEditable),
    ),
    defaultValues: saleToForm(sale, timeZone),
  });
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      const saved = await update.mutateAsync({
        id: sale.id,
        body: toUpdateSale(values, timeZone, scheduleEditable),
      });
      form.reset(saleToForm(saved, timeZone));
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, {
          knownFields: KNOWN_FIELDS,
          mapField: mapSaleField,
        })
      ) {
        return;
      }
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  useSaveBar({ form, onSave: submit, saving: update.isPending });
  const disabled = !canWrite;

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <FormSection
            title={t('auctions:sales.sections.schedule')}
            description={
              scheduleEditable
                ? t('auctions:sales.sections.scheduleDescription', { zone: timeZone })
                : t('auctions:sales.sections.scheduleLocked')
            }
          >
            <FormField
              control={form.control}
              name="startsAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.startsAt')}</FormLabel>
                  <FormControl>
                    <Input
                      type="datetime-local"
                      disabled={disabled || !scheduleEditable}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="firstCloseAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.firstCloseAt')}</FormLabel>
                  <FormControl>
                    <Input
                      type="datetime-local"
                      disabled={disabled || !scheduleEditable}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lotIntervalMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.interval')}</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      disabled={disabled || !scheduleEditable}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('auctions:sales.fields.intervalHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="buyerPremiumPercent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:fields.premium')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        inputMode="decimal"
                        className="pr-8"
                        disabled={disabled || !scheduleEditable}
                        {...field}
                      />
                      <span
                        aria-hidden
                        className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm"
                      >
                        %
                      </span>
                    </div>
                  </FormControl>
                  <FormDescription>{t('auctions:sales.fields.premiumHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {sale.lastCloseAt ? (
              <p className="text-muted-foreground text-xs" title={format.utc(sale.lastCloseAt)}>
                {t('auctions:sales.fields.lastClose', { when: format.dateTime(sale.lastCloseAt) })}
              </p>
            ) : null}
            {canWrite ? (
              <div className="flex items-center gap-2 border-t pt-4">
                <Button
                  type="submit"
                  form={SALE_FORM_ID}
                  disabled={update.isPending || !form.formState.isDirty}
                  className="flex-1"
                >
                  {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={update.isPending || !form.formState.isDirty}
                  onClick={() => form.reset()}
                >
                  {t('auctions:sales.actions.discard')}
                </Button>
              </div>
            ) : null}
          </FormSection>
        }
      >
        <form id={SALE_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            title={t('auctions:sales.sections.details')}
            description={t('auctions:sales.sections.detailsDescription')}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.title')}</FormLabel>
                  <FormControl>
                    <Input disabled={disabled} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.handle')}</FormLabel>
                  <FormControl>
                    <Input
                      className="font-mono text-[13px]"
                      disabled={disabled || !scheduleEditable}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {scheduleEditable
                      ? t('auctions:sales.fields.handleHint')
                      : t('auctions:sales.fields.handleLocked')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="subtitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.subtitle')}</FormLabel>
                  <FormControl>
                    <Input disabled={disabled} {...field} />
                  </FormControl>
                  <FormDescription>{t('auctions:sales.fields.subtitleHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="viewing"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.viewing')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} disabled={disabled} {...field} />
                  </FormControl>
                  <FormDescription>{t('auctions:sales.fields.viewingHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            title={t('auctions:sales.sections.essay')}
            description={t('auctions:sales.sections.essayDescription')}
          >
            <FormField
              control={form.control}
              name="essay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t('auctions:sales.sections.essay')}</FormLabel>
                  <FormControl>
                    <MediaRichTextEditor
                      value={field.value}
                      onChange={(html) => field.onChange(html ?? '')}
                      onBlur={field.onBlur}
                      aria-label={t('auctions:sales.sections.essay')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            title={t('auctions:sales.sections.cover')}
            description={t('auctions:sales.sections.coverDescription')}
          >
            <FormField
              control={form.control}
              name="coverMediaId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t('auctions:sales.sections.cover')}</FormLabel>
                  <MediaIdField
                    value={field.value}
                    onChange={(mediaId) => {
                      field.onChange(mediaId);
                      form.clearErrors('coverMediaId');
                    }}
                    label={t('auctions:sales.fields.coverLabel')}
                    disabled={disabled}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
        </form>
      </FormLayout>
    </Form>
  );
}
